class FacetShort extends HTMLSelectElement {
  constructor() {
    super();
    this.initMotionInView();
    this.addEventListener('change', this.onSelectChange.bind(this));
  }

  get options() {
    return this.getElementsByTagName('option');
  }

  get fakeSelectElement() {
    return this.previousElementSibling;
  }

  initMotionInView() {
    FoxTheme.Motion.inView(this, this.calcSelectWidth.bind(this), { margin: '200px 0px 200px 0px' });
  }

  calcSelectWidth() {
    const selectStyle = window.getComputedStyle(this);
    const selectedOptionText = this.options[this.selectedIndex].text;

    const textElement = this.createTextElement(selectedOptionText, selectStyle);
    document.body.appendChild(textElement);
    const selectWidth = textElement.offsetWidth;
    textElement.remove();

    this.style.setProperty('--width', `${selectWidth + 22}px`);
  }

  createTextElement(text, style) {
    const textElement = document.createElement('span');
    textElement.style.fontFamily = style.fontFamily;
    textElement.style.fontSize = style.fontSize;
    textElement.style.fontWeight = style.fontWeight;
    textElement.style.visibility = 'hidden';
    textElement.style.position = 'absolute';
    textElement.innerHTML = text;
    return textElement;
  }

  onSelectChange(event) {
    this.calcSelectWidth();
    this.updateFormParams(event);
  }

  updateFormParams(event) {
    const form = this.getClosestFacetForm() || this.getFirstFacetForm();

    if (form) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      url.searchParams.delete('page');
      form.renderSection(url.toString(), event);
    }
  }

  getClosestFacetForm() {
    return this.closest('form[is="facet-form"]');
  }

  getFirstFacetForm() {
    return document.querySelector('form[is="facet-form"]');
  }
}

customElements.define('facet-short', FacetShort, { extends: 'select' });
class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minRangeInput = this.querySelector('input[type="range"]:first-child');
    this.maxRangeInput = this.querySelector('input[type="range"]:last-child');
    // Support any range filter (not only price) by falling back to the first/last number inputs.
    // Price filter still works because its inputs are the only number inputs inside the element.
    const numberInputs = this.querySelectorAll('input[type="number"]');
    this.minValueInput =
      this.querySelector('input[name="filter.v.price.gte"]') ||
      this.querySelector('input[name$=".gte"]') ||
      numberInputs[0];
    this.maxValueInput =
      this.querySelector('input[name="filter.v.price.lte"]') ||
      this.querySelector('input[name$=".lte"]') ||
      numberInputs[1];

    if (!this.minRangeInput || !this.maxRangeInput || !this.minValueInput || !this.maxValueInput) return;

    this.minValueInput.addEventListener('focus', this.minValueInput.select);
    this.maxValueInput.addEventListener('focus', this.maxValueInput.select);
    this.minValueInput.addEventListener('change', this.handleInputMinChange.bind(this));
    this.maxValueInput.addEventListener('change', this.handleInputMaxChange.bind(this));

    this.minRangeInput.addEventListener('change', this.handleRangeMinChange.bind(this));
    this.maxRangeInput.addEventListener('change', this.handleRangeMaxChange.bind(this));
    this.minRangeInput.addEventListener('input', this.handleRangeMinInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.handleRangeMaxInput.bind(this));
  }

  handleInputMinChange(event) {
    event.preventDefault();
    const max = parseInt(this.maxValueInput.value || event.target.max);
    event.target.value = Math.max(Math.min(parseInt(event.target.value), max), parseInt(event.target.min));
    this.minRangeInput.value = event.target.value;
    this.minRangeInput.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(this.minRangeInput.value) / parseInt(this.minRangeInput.max)) * 100}%`
    );
  }

  handleInputMaxChange(event) {
    event.preventDefault();
    const min = parseInt(this.minValueInput.value || event.target.min);
    event.target.value = Math.min(Math.max(parseInt(event.target.value), min), parseInt(event.target.max));
    this.maxRangeInput.value = event.target.value;
    this.maxRangeInput.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(this.maxRangeInput.value) / parseInt(this.maxRangeInput.max)) * 100}%`
    );
  }

  handleRangeMinChange(event) {
    event.stopPropagation();
    this.minValueInput.value = event.target.value;
    this.minValueInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMaxChange(event) {
    event.stopPropagation();
    this.maxValueInput.value = event.target.value;
    this.maxValueInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMinInput(event) {
    const max = parseInt(this.maxValueInput.value || event.target.max);
    event.target.value = Math.min(parseInt(event.target.value), max);
    event.target.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.minValueInput.value = event.target.value;
  }

  handleRangeMaxInput(event) {
    const min = parseInt(this.minValueInput.value || event.target.min);
    event.target.value = Math.max(parseInt(event.target.value), min);
    event.target.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(event.target.value) / parseInt(event.target.max)) * 100}%`
    );
    this.maxValueInput.value = event.target.value;
  }
}
customElements.define('price-range', PriceRange);

class VolumeRange extends HTMLElement {
  constructor() {
    super();
    this.submitTimeout = null;
  }

  connectedCallback() {
    this.rangeMin = this.querySelector('[data-volume-min]');
    this.rangeMax = this.querySelector('[data-volume-max]');
    this.inputMin = this.querySelector('[data-volume-input-min]');
    this.inputMax = this.querySelector('[data-volume-input-max]');
    this.checkboxContainer = this.querySelector('[data-volume-checkboxes]');
    this.wrapper = this.querySelector('.facet-range-wrapper');
    this.filterParam = this.getAttribute('data-filter-param') || '';
    this.storageKey = `volumeRangeValues:${this.filterParam || 'default'}`;

    if (!this.rangeMin || !this.rangeMax || !this.inputMin || !this.inputMax || !this.checkboxContainer || !this.wrapper) return;

    this.minValue = parseInt(this.rangeMin.min);
    this.maxValue = parseInt(this.rangeMax.max);

    this.setupEventListeners();

    // Restore previously chosen range (helps keep thumbs stable across facet AJAX re-renders).
    this.restoreFromStorage();

    // Ensure checkbox state is consistent with current UI state on initial render.
    this.updateProgressBar();

    // Only update checkboxes if the user has actively interacted with the volume range
    // (i.e. there are already active/checked checkboxes or stored values).
    // This prevents all volume checkboxes from being checked on page load
    // which would pollute other filter submissions.
    const hasActiveCheckboxes = this.checkboxContainer.querySelectorAll('input[type="checkbox"]:checked').length > 0;
    const hasStoredValues = !!sessionStorage.getItem(this.storageKey);
    if (hasActiveCheckboxes || hasStoredValues) {
      this.updateCheckboxes();
    }
  }

  setupEventListeners() {
    this.rangeMin.addEventListener('input', (e) => {
      this.handleRangeMinInput(e);
    });
    this.rangeMax.addEventListener('input', (e) => {
      this.handleRangeMaxInput(e);
    });

    // Submit on 'change' (mouseup / blur) so the slider doesn't get re-rendered mid-drag.
    this.rangeMin.addEventListener('change', () => this.commitAndSubmit());
    this.rangeMax.addEventListener('change', () => this.commitAndSubmit());

    this.inputMin.addEventListener('change', (e) => {
      this.handleInputMinChange(e);
      this.commitAndSubmit();
    });
    this.inputMax.addEventListener('change', (e) => {
      this.handleInputMaxChange(e);
      this.commitAndSubmit();
    });
  }

  storeValues(min, max) {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify({ min, max }));
    } catch (e) {
      // ignore
    }
  }

  restoreFromStorage() {
    try {
      const stored = sessionStorage.getItem(this.storageKey);
      if (!stored) return;
      const parsed = JSON.parse(stored);
      if (!parsed) return;

      const clamped = this.clampMinMax(parseInt(parsed.min), parseInt(parsed.max));
      this.rangeMin.value = clamped.min;
      this.rangeMax.value = clamped.max;
      this.inputMin.value = clamped.min;
      this.inputMax.value = clamped.max;
    } catch (e) {
      // ignore
    }
  }

  commitAndSubmit() {
    // Ensure UI is consistent before submission
    this.handleRangeMinInput(new Event('input'));
    this.handleRangeMaxInput(new Event('input'));

    // Check the discrete volume values within the dragged range.
    this.updateCheckboxes();

    // Snap the slider/inputs to the actual selected values so the UI matches
    // the applied filter (and the active-filter chip) instead of the raw drag
    // position — e.g. a drag to 206 settles on the real max value of 200.
    const snapped = this.getCheckedRange();
    if (snapped) {
      const clamped = this.clampMinMax(snapped.min, snapped.max);
      this.rangeMin.value = clamped.min;
      this.rangeMax.value = clamped.max;
      this.inputMin.value = clamped.min;
      this.inputMax.value = clamped.max;
      this.updateProgressBar();
      this.storeValues(clamped.min, clamped.max);
    } else {
      this.storeValues(parseInt(this.inputMin.value), parseInt(this.inputMax.value));
    }

    this.submitForm();
  }

  getCheckedRange() {
    const checked = this.checkboxContainer.querySelectorAll('input[type="checkbox"]:checked');
    if (checked.length === 0) return null;
    let min = Infinity;
    let max = -Infinity;
    checked.forEach((cb) => {
      const v = parseInt(cb.getAttribute('data-volume-value'));
      if (isNaN(v)) return;
      if (v < min) min = v;
      if (v > max) max = v;
    });
    if (min === Infinity || max === -Infinity) return null;
    return { min, max };
  }

  clampMinMax(min, max) {
    let nextMin = isNaN(min) ? this.minValue : min;
    let nextMax = isNaN(max) ? this.maxValue : max;

    nextMin = Math.max(this.minValue, Math.min(nextMin, this.maxValue));
    nextMax = Math.max(this.minValue, Math.min(nextMax, this.maxValue));

    if (nextMin > nextMax) nextMin = nextMax;
    return { min: nextMin, max: nextMax };
  }

  handleRangeMinInput(e) {
    if (e && e.stopPropagation) e.stopPropagation();
    const min = parseInt(this.rangeMin.value);
    const max = parseInt(this.rangeMax.value);
    const clamped = this.clampMinMax(min, max);

    this.rangeMin.value = clamped.min;
    this.rangeMax.value = clamped.max;
    this.inputMin.value = clamped.min;
    this.inputMax.value = clamped.max;
    this.updateProgressBar();
  }

  handleRangeMaxInput(e) {
    if (e && e.stopPropagation) e.stopPropagation();
    const min = parseInt(this.rangeMin.value);
    const max = parseInt(this.rangeMax.value);
    const clamped = this.clampMinMax(min, max);

    this.rangeMin.value = clamped.min;
    this.rangeMax.value = clamped.max;
    this.inputMin.value = clamped.min;
    this.inputMax.value = clamped.max;
    this.updateProgressBar();
  }

  handleInputMinChange(e) {
    e.stopPropagation();
    const min = parseInt(this.inputMin.value);
    const max = parseInt(this.inputMax.value);
    const clamped = this.clampMinMax(min, max);

    this.inputMin.value = clamped.min;
    this.inputMax.value = clamped.max;
    this.rangeMin.value = clamped.min;
    this.rangeMax.value = clamped.max;
    this.updateProgressBar();
  }

  handleInputMaxChange(e) {
    e.stopPropagation();
    const min = parseInt(this.inputMin.value);
    const max = parseInt(this.inputMax.value);
    const clamped = this.clampMinMax(min, max);

    this.inputMin.value = clamped.min;
    this.inputMax.value = clamped.max;
    this.rangeMin.value = clamped.min;
    this.rangeMax.value = clamped.max;
    this.updateProgressBar();
  }

  updateProgressBar() {
    const min = parseInt(this.rangeMin.value);
    const max = parseInt(this.rangeMax.value);
    const range = this.maxValue - this.minValue;
    if (range === 0) return;

    const lowerPercent = ((min - this.minValue) / range) * 100;
    const higherPercent = ((max - this.minValue) / range) * 100;

    this.wrapper.style.setProperty('--range-min', `${lowerPercent}%`);
    this.wrapper.style.setProperty('--range-max', `${higherPercent}%`);
  }

  updateCheckboxes() {
    const min = parseInt(this.inputMin.value);
    const max = parseInt(this.inputMax.value);
    const checkboxes = this.checkboxContainer.querySelectorAll('input[type="checkbox"]');

    checkboxes.forEach((checkbox) => {
      if (checkbox.disabled) return;
      const value = parseInt(checkbox.getAttribute('data-volume-value'));
      checkbox.checked = value >= min && value <= max;
    });
  }

  submitForm() {
    const form = this.closest('form[is="facet-form"]') || this.closest('form');
    if (!form) return;
    form.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

customElements.define('volume-range', VolumeRange);

class FacetForm extends HTMLFormElement {
  constructor() {
    super();

    this.dirty = false;
    this.cachedMap = new Map();

    this.addEventListener('change', this.onFormChange);
    this.addEventListener('submit', this.onFormSubmit);
  }

  onFormChange() {
    this.dirty = true;
    this.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  onFormSubmit(event) {
    event.preventDefault();
    if (!this.dirty) return;

    const url = this.buildUrl().toString();
    this.renderSection(url, event);
  }

  buildUrl() {
    const searchParams = new URLSearchParams(new FormData(this));
    const url = new URL(this.action);

    url.search = '';
    searchParams.forEach((value, key) => url.searchParams.append(key, value));

    // Remove empty params (covers all range filters, not just price).
    Array.from(url.searchParams.keys()).forEach((key) => {
      if (url.searchParams.get(key) === '') url.searchParams.delete(key);
    });
    if (url.searchParams.get('page') === '') url.searchParams.delete('page');

    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  updateURLHash(url) {
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete('section_id');
    history.replaceState({}, '', clonedUrl.toString());
  }

  beforeRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([[container, { y: translateY, opacity: 0 }, { duration: 0 }]]);

    setTimeout(() => {
      const target = document.querySelector('.collection');
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - 95,
        behavior: FoxTheme.config.motionReduced ? 'auto' : 'smooth',
      });
      if (loadings) {
        loadings.forEach((loading) => {
          loading.classList.add('btn--loading');
        });
      }
    }, 100);
  }

  afterRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const items = container.querySelectorAll('.product-card');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([
      [container, { y: [translateY, 0], opacity: [0, 1] }],
      [
        items,
        { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
        { duration: 0.5, delay: FoxTheme.config.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1) },
      ],
    ]);

    if (loadings) {
      loadings.forEach((loading) => {
        loading.classList.remove('btn--loading');
      });
    }

    document.dispatchEvent(new CustomEvent('collection:rerendered'));
  }

  renderSection(url, event) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url, event) : this.renderSectionFromFetch(url, event);

    if (this.hasAttribute('data-history')) this.updateURLHash(url);

    this.dirty = false;
  }

  renderSectionFromFetch(url, event) {
    this.beforeRenderSection();
    const start = performance.now();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const execution = performance.now() - start;

        setTimeout(
          () => {
            this.renderFilters(responseText, event);
            this.renderFiltersActive(responseText);
            const didUpdateGrid = this.renderProductGridContainer(responseText);
            this.renderProductCount(responseText);
            this.renderSortBy(responseText);
            this.renderSortByMobile(responseText);

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });
            this.cachedMap.set(url, responseText);

            this.afterRenderSection();

            // If the AJAX response doesn't include a product grid, fall back to full navigation.
            if (!didUpdateGrid) {
              window.location.href = url;
            }
          },
          execution > 250 ? 0 : 250
        );
      });
  }

  renderSectionFromCache(url, event) {
    this.beforeRenderSection();

    setTimeout(() => {
      const responseText = this.cachedMap.get(url);
      this.renderFilters(responseText, event);
      this.renderFiltersActive(responseText);
      const didUpdateGrid = this.renderProductGridContainer(responseText);
      this.renderProductCount(responseText);
      this.renderSortBy(responseText);
      this.renderSortByMobile(responseText);

      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: responseText });

      this.afterRenderSection();

      // If the cached response doesn't include a product grid, fall back to full navigation.
      if (!didUpdateGrid) {
        window.location.href = url;
      }
    }, 250);
  }

  renderFilters(responseText, event) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const facetElements = parsedHTML.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter]'
    );

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('[data-filter]') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

    facetsToRender.forEach((element) => {
      const filter = document.querySelector(`[data-filter][data-index="${element.dataset.index}"]`);
      if (filter !== null) {
        if (filter.tagName === 'DETAILS') {
          filter.querySelector('summary + *').innerHTML = element.querySelector('summary + *').innerHTML;
        } else {
          filter.innerHTML = element.innerHTML;
        }
      }
    });
  }

  renderFiltersActive(responseText) {
    const id = 'FacetFiltersActive';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderProductGridContainer(responseText) {
    const id = 'ProductGridContainer';
    const container = document.getElementById(id);
    if (!container) return false;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const nextContainer = parsedHTML.getElementById(id);
    if (!nextContainer) return false;
    container.innerHTML = nextContainer.innerHTML;

    const layoutSwitcher = document.querySelector('#ProductGridWrapper layout-switcher');
    if (layoutSwitcher) layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.btn--active'));
    return true;
  }

  renderProductCount(responseText) {
    const id = 'ProductCount';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortBy(responseText) {
    const id = 'SortByContainer';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }

  renderSortByMobile(responseText) {
    const id = 'SortByContainerMobile';
    if (document.getElementById(id) === null) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

    document.getElementById(id).innerHTML = parsedHTML.getElementById(id) && parsedHTML.getElementById(id).innerHTML;
  }
}
customElements.define('facet-form', FacetForm, { extends: 'form' });

class FacetRemove extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick);
  }

  onClick(event) {
    const form = this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

    if (form) {
      event.preventDefault();

      const url = new URL(this.href);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      form.renderSection(url.toString(), event);
    }
  }
}

customElements.define('facet-remove', FacetRemove, { extends: 'a' });

class FacetCount extends HTMLElement {
  constructor() {
    super();
  }

  facetUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.facetUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
      this.onFacetUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.facetUpdateUnsubscriber) {
      this.facetUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText.replace(/\D/g, ''));
  }

  onFacetUpdate(event) {
    const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
    const facetCount = parsedHTML.querySelector('facet-count');
    this.innerText = facetCount && facetCount.innerHTML;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('facet-count', FacetCount);

class LoadMoreButton extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClickHandler);

    if (this.getAttribute('type') == 'infinite') {
      FoxTheme.Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick() {
    if (this.classList.contains('btn--loading')) return;
    this.loadingState();

    const url = this.setUrl().toString();

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((responseText) => {
        this.renderPagination(responseText);
        this.renderProductGridContainer(responseText);
        this.resetLoadingState();
      })
      .catch((error) => {
        console.error('Load more failed:', error);
        this.resetLoadingState();
      });
  }

  renderPagination(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const pagination = productGridContainer.querySelector('.pagination');
    const source = parsedHTML.querySelector('.pagination');

    if (source) {
      pagination.innerHTML = source.innerHTML;
    } else {
      pagination.remove();
    }
  }

  renderProductGridContainer(responseText) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const productList = productGridContainer.querySelector('.products-list');
    const gridList = productGridContainer.querySelector('grid-list');
    const source = parsedHTML.querySelector('.products-list');

    if (source && productList) {
      source.querySelectorAll('.card').forEach((item) => {
        productList.appendChild(item);
      });

      gridList && gridList.reload();
    }
  }

  setUrl() {
    const action = this.getAttribute('action');
    const url = new URL(action, window.location.origin);
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  loadingState() {
    this.classList.add('pointer-events-none');
    this.classList.add('btn--loading');
  }

  resetLoadingState() {
    this.classList.remove('pointer-events-none');
    this.classList.remove('btn--loading');
  }
}

customElements.define('load-more-button', LoadMoreButton, { extends: 'button' });

class LayoutSwitcher extends HTMLElement {
  constructor() {
    super();
    this.cookieName = 'sleektheme:collection-layout';

    this.initLayoutMode();
    this.buttons.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
  }

  get buttons() {
    return this.querySelectorAll('button');
  }

  onButtonClick(event) {
    const target = event.target ? event.target : event;
    this.changeLayout(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (FoxTheme.config.hasLocalStorage) {
      const layoutMode = window.localStorage.getItem(this.cookieName);

      if (layoutMode !== null) {
        const target = this.querySelector(`button[data-layout-mode="${layoutMode}"]`);

        if (target) {
          this.changeLayout(target, layoutMode);
        }
      }
    }
  }

  changeLayout(target, layoutMode) {
    const productGrid = document.getElementById('ProductsList');
    if (!productGrid) return;
    const removedClass = ['f-grid-1-cols', 'f-grid-2-cols'];
    removedClass.forEach((removed) => {
      productGrid.classList.remove(removed);
    });

    let addedClass = '';
    switch (layoutMode) {
      case 'grid-1':
        addedClass = 'f-grid-1-cols';
        break;
      case 'grid-2':
        addedClass = 'f-grid-2-cols';
        break;
    }
    productGrid.classList.add(addedClass);
    this.buttons.forEach((button) => {
      button.classList.remove('btn--active');
    });

    target.classList.add('btn--active');

    if (FoxTheme.config.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }
}

customElements.define('layout-switcher', LayoutSwitcher);
